"use client"
import { type FragmentOf, readFragment } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Flex,
  Form,
  Media,
  Separator,
  SkeletonBlock,
  Tooltip,
  tv,
} from "@opensea/ui-kit"
import { useLocalStorage } from "@opensea/ui-kit/hooks"
import { useMemo } from "react"
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form"
import { useTranslations } from "use-intl"
import { Button } from "../../../../../components/Button"
import {
  CreateOrdersSummary,
  CreateOrdersSummarySkeleton,
} from "../../../../../components/CreateOrdersSummary"
import { ExpirationSelect } from "../../../../../components/ExpirationSelect"
import { useIsFormSubmitDisabled } from "../../../../../lib/form/valid"
import {
  DEFAULT_EXPIRATION,
  MAX_NUM_TRAITS_FOR_MULTI_TRAIT_OFFERS,
  OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE,
  OFFER_EXPIRATION_LOCAL_STORAGE,
} from "../../../constants"
import type { CreateCollectionOfferFormValues } from "../../types"
import {
  CreateCollectionOfferFlowTable,
  CreateCollectionOfferFlowTableSkeleton,
} from "../CollectionOfferFlowTable/CreateCollectionOfferFlowTable"
import {
  CreateCollectionOfferFlowHeaderActions,
  CreateCollectionOfferFlowHeaderActionsSkeleton,
} from "../CreateCollectionOfferFlowHeaderActions"
import { CreateCollectionOfferTraitInput } from "../CreateCollectionOfferTraitInput"
import { CreateCollectionOfferFormFragment } from "./CreateCollectionOfferForm.fragment"

type Props = {
  collection: FragmentOf<typeof CreateCollectionOfferFormFragment>
  onSubmit: (values: CreateCollectionOfferFormValues) => void
}

const footerVariants = tv({
  slots: {
    buttonContainer:
      "grid grid-cols-2 gap-3 pb-4 md:flex md:justify-end md:pb-0",
  },
})

export const CreateCollectionOfferForm = ({
  collection: collectionKey,
  onSubmit,
}: Props) => {
  const t = useTranslations("wallet.CreateCollectionOfferForm")
  const collection = readFragment(
    CreateCollectionOfferFormFragment,
    collectionKey,
  )
  const { control, handleSubmit } =
    useFormContext<CreateCollectionOfferFormValues>()
  const { errors } = useFormState({ control })
  const offers = useWatch({ control, name: "offers" })
  const firstOffer = offers[0]
  const [_, setOfferExpirationMinutes] = useLocalStorage(
    OFFER_EXPIRATION_LOCAL_STORAGE,
    DEFAULT_EXPIRATION,
  )

  const [__, setOfferExpirationCustom] = useLocalStorage<string | undefined>(
    OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE,
    undefined,
  )

  const isFormSubmitDisabled = useIsFormSubmitDisabled(control)

  const traitOfferValidation = useMemo(() => {
    const traits = firstOffer?.traits || []
    if (traits.length === 0) {
      return { isValid: true, reason: undefined }
    }

    // Check if there are multiple values for the same trait type
    const traitTypeCounts = new Map<string, number>()
    for (const trait of traits) {
      const count = traitTypeCounts.get(trait.traitType) || 0
      traitTypeCounts.set(trait.traitType, count + 1)
    }

    const hasMultipleValuesPerType = Array.from(traitTypeCounts.values()).some(
      count => count > 1,
    )

    if (hasMultipleValuesPerType) {
      return {
        isValid: false,
        reason: t("multipleValuesPerTrait"),
      }
    }

    // Check if there are too many trait types
    if (traitTypeCounts.size > MAX_NUM_TRAITS_FOR_MULTI_TRAIT_OFFERS) {
      return {
        isValid: false,
        reason: t("tooManyTraits", {
          max: MAX_NUM_TRAITS_FOR_MULTI_TRAIT_OFFERS,
        }),
      }
    }

    return { isValid: true, reason: undefined }
  }, [firstOffer?.traits, t])

  const footer = useMemo(() => {
    return (
      <Flex className={footerVariants().buttonContainer()}>
        <Controller
          control={control}
          name="expiration"
          render={({ field }) => (
            <ExpirationSelect
              className="w-full"
              customDate={field.value.customExpiration}
              error={errors.expiration?.customExpiration?.message}
              onChange={value => {
                field.onChange({
                  ...field.value,
                  expirationType: value,
                })
                setOfferExpirationMinutes(value)
              }}
              onCustomDateChange={date => {
                field.onChange({
                  ...field.value,
                  customExpiration: date,
                })
                setOfferExpirationCustom(date.toISOString())
              }}
              value={field.value.expirationType}
            />
          )}
        />

        {firstOffer?.traits &&
        firstOffer.traits.length > 0 &&
        !traitOfferValidation.isValid ? (
          <Tooltip content={traitOfferValidation.reason || ""}>
            <span>
              <Button disabled type="submit">
                {t("reviewTraitOffer")}
              </Button>
            </span>
          </Tooltip>
        ) : firstOffer?.trait ||
          (firstOffer?.traits &&
            firstOffer.traits.length > 0 &&
            traitOfferValidation.isValid) ? (
          <Button disabled={isFormSubmitDisabled} type="submit">
            {t("reviewTraitOffer")}
          </Button>
        ) : (
          <Button disabled={isFormSubmitDisabled} type="submit">
            {t("reviewCollectionOffer")}
          </Button>
        )}
      </Flex>
    )
  }, [
    control,
    isFormSubmitDisabled,
    firstOffer?.trait,
    errors.expiration?.customExpiration?.message,
    setOfferExpirationMinutes,
    setOfferExpirationCustom,
    t,
    traitOfferValidation.isValid,
    traitOfferValidation.reason,
    firstOffer?.traits?.length,
    firstOffer?.traits,
  ])

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <BottomSheetHeader className="justify-between">
        <BottomSheetHeaderTitle className="whitespace-nowrap">
          {firstOffer?.trait ||
          (firstOffer?.traits && firstOffer.traits.length > 0)
            ? t("createTraitOffer")
            : t("createCollectionOffer")}
        </BottomSheetHeaderTitle>
        <Media greaterThanOrEqual="md">
          <CreateCollectionOfferFlowHeaderActions />
        </Media>
      </BottomSheetHeader>

      <BottomSheetBody className="mt-2 pb-0">
        <Media lessThan="md">
          <CreateCollectionOfferFlowHeaderActions className="pb-4" />
        </Media>

        <CreateCollectionOfferFlowTable
          collection={collection}
          isReview={false}
        />

        <Separator className="mt-1" />

        <CreateCollectionOfferTraitInput
          collection={collection}
          traitEnabled={Boolean(collection.traitOffersEnabled)}
        />
        <Separator />

        <CreateOrdersSummary
          adjustForFees={false}
          className="md:w-fit"
          collection={collection}
          creatorFeeEnforced={firstOffer?.isCreatorFeeRequired}
          currencyIdentifier={{
            chainIdentifier: collection.chain.identifier,
            contractAddress: collection.chain.offerCurrency.address,
          }}
          orderSide="offer"
          orders={offers}
          symbol={collection.chain.offerCurrency.symbol}
        />

        <Media lessThan="md">{footer}</Media>
      </BottomSheetBody>

      <Media greaterThanOrEqual="md">
        <BottomSheetFooter>{footer}</BottomSheetFooter>
      </Media>
    </Form>
  )
}

export const CreateCollectionOfferFormSkeleton = () => {
  const t = useTranslations("wallet.CreateCollectionOfferForm")
  return (
    <>
      <BottomSheetHeader className="justify-between">
        <BottomSheetHeaderTitle>
          {t("createCollectionOffer")}
        </BottomSheetHeaderTitle>
        <Media greaterThanOrEqual="md">
          <CreateCollectionOfferFlowHeaderActionsSkeleton />
        </Media>
      </BottomSheetHeader>
      <BottomSheetBody>
        <Media lessThan="md">
          <CreateCollectionOfferFlowHeaderActionsSkeleton className="pb-4" />
        </Media>
        <CreateCollectionOfferFlowTableSkeleton />
        <Separator className="mt-1" />
        <CreateOrdersSummarySkeleton orderSide="offer" />
      </BottomSheetBody>
      <BottomSheetFooter>
        <Flex className={footerVariants().buttonContainer()}>
          <SkeletonBlock className="h-10 w-full rounded md:w-44" />
          <SkeletonBlock className="h-10 w-full rounded md:w-24" />
        </Flex>
      </BottomSheetFooter>
    </>
  )
}
